import { ScrollReveal } from '@/components/design-system';
import { EmptyState } from '@/components/poof-ui';
import PickupCard from '@/components/company/PickupCard';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyListings, type ListingsResponse } from '@/lib/collections/listings';
import { CalendarClock, CheckCircle2, Clock, Truck } from 'lucide-react';

interface MyPickupsPanelProps {
  userAddress: string;
}

const SECTIONS: { status: 'accepted' | 'scheduled' | 'completed'; label: string; icon: typeof Clock }[] = [
  { status: 'accepted', label: 'Accepted — needs scheduling', icon: Clock },
  { status: 'scheduled', label: 'Scheduled — ready to settle', icon: CalendarClock },
  { status: 'completed', label: 'Completed', icon: CheckCircle2 },
];

/** Realtime view of every listing this company has accepted, grouped by pipeline stage. */
export function MyPickupsPanel({ userAddress }: MyPickupsPanelProps) {
  const { data: listings, loading } = useRealtimeData<ListingsResponse[]>(subscribeManyListings, true, '');

  const mine = (listings ?? []).filter((l) => l.acceptedBy === userAddress);

  if (!loading && mine.length === 0) {
    return (
      <EmptyState
        icon={<Truck />}
        title="No pickups yet"
        description="Accept an open listing to start your first pickup."
      />
    );
  }

  return (
    <div className="space-y-8">
      {SECTIONS.map(({ status, label, icon: Icon }) => {
        const group = mine.filter((l) => l.status === status);
        if (group.length === 0) return null;
        return (
          <div key={status}>
            <div className="mb-3 flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold text-foreground">{label}</h3>
              <span className="text-xs text-muted-foreground">({group.length})</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {group.map((listing, i) => (
                <ScrollReveal key={listing.id} delay={Math.min(i * 0.04, 0.25)}>
                  <PickupCard listing={listing} userAddress={userAddress} />
                </ScrollReveal>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default MyPickupsPanel;
